import React, { useState, useEffect } from 'react';
import { Google as GoogleIcon, Microsoft } from '@mui/icons-material';
import { auth, googleProvider, microsoftProvider, appCheck } from '../firebaseConfig';
import {
  createUserWithEmailAndPassword,
  signInWithPopup,
  sendEmailVerification,
  onAuthStateChanged,
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { getToken } from 'firebase/app-check';
import { useLocation } from 'react-router-dom';
import './SignupPage.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useLocalizedPath } from '../utils/navigation';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
// import { Turnstile } from '@marsidev/react-turnstile'; // --- 1. COMMENTED OUT
import { useAnonymousUser } from '../context/AnonymousUserContext'; // === IMPORT THE ANONYMOUS USER HOOK ===
import { markDeviceAsUsed } from '../hitscheck'; // === IMPORT THE NEW "BURN" FUNCTION ===
import { useTranslation } from 'react-i18next';
import LocalizedLink from './LocalizedLink';
import StoreBadges from './StoreBadges';
import { SEO } from './SEO';
import { pickRandomHeroImage, pickRandomVariantIndex } from '../config/adHeroImages';
import {
  cacheAuthUser,
  clearCachedAuthUser,
  ensureAuthUserDoc,
  ensureUserErrorMessage,
  isEnsureUserBlockedError,
  providerIdForUser,
} from '../utils/authUserDoc';
import {
  buildAdIntentSearchParams,
  clearPendingAdMode,
  getAdModeFromSearchParams,
  getPostAuthAdDestination,
  readPendingAdMode,
  storePendingAdMode,
} from '../utils/adLandingIntent';

const RESEND_COOLDOWN_SECONDS = 60;
function formatResendWait(seconds: number) {
  if (seconds >= 60) {
    const minutes = Math.floor(seconds / 60);
    const remainder = seconds % 60;
    return remainder > 0 ? `${minutes}m ${remainder}s` : `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  return `${seconds} second${seconds === 1 ? '' : 's'}`;
}

/** Redirect to a pending enterprise invite if one exists, otherwise to /chat. */
function redirectAfterLogin(navigate: ReturnType<typeof import('../hooks/useLocalizedNavigate').useLocalizedNavigate>) {
  const pending = sessionStorage.getItem('pendingEnterpriseInvite');
  if (pending) {
    navigate(`/invite/${pending}`);
    return;
  }

  const pendingAdMode = readPendingAdMode();
  if (pendingAdMode) {
    clearPendingAdMode();
    navigate(getPostAuthAdDestination(pendingAdMode));
    return;
  }

  navigate('/chat');
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface Errors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
}

const SignupPage: React.FC = () => {
  // === GET THE DEVICE STATUS FROM THE CONTEXT ===
  const { isBlocked, isLoading: isDeviceCheckLoading } = useAnonymousUser();
  const { t } = useTranslation(['auth', 'nav', 'footer']);

  const trackSignupEvent = (method: string) => {
    const gtag = (window as any)?.gtag as undefined | ((...args: any[]) => void);
    if (typeof gtag === 'function') {
      gtag('event', 'sign_up', { method });
    }
  };

  const [isEmailSignUp, setIsEmailSignUp] = useState(false);
  const [isVerificationSent, setIsVerificationSent] = useState(false);
  const [verified, setVerified] = useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<Errors>({});
  // const [turnstileToken, setTurnstileToken] = useState<string | null>(null); // --- 2. COMMENTED OUT
  const [loading, setLoading] = useState(false);
  const [oobCode, setOobCode] = useState<string | null>(null);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const [resendLimitReached, setResendLimitReached] = useState(false);
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  const localizedPath = useLocalizedPath();
  const locationSearchParams = new URLSearchParams(location.search);
  const requestedMode = getAdModeFromSearchParams(locationSearchParams);
  const forwardedAuthParams = buildAdIntentSearchParams(locationSearchParams);
  const isAdTraffic = requestedMode !== null;
  const adHeroKey = requestedMode ? requestedMode.replace(/-/g, '_') : null;
  const [heroImage] = useState<string | null>(() => (requestedMode ? pickRandomHeroImage(requestedMode) : null));
  const [isHeroLoaded, setIsHeroLoaded] = useState(() => heroImage === null);
  const heroVariants = adHeroKey
    ? (t(`auth.hero.${adHeroKey}.variants`, { returnObjects: true }) as unknown as Array<{ title: string; subtitle: string }>)
    : [];
  const heroVariantsArray: Array<{ title: string; subtitle: string }> = Array.isArray(heroVariants) ? heroVariants : [];
  const [heroVariantIndex] = useState<number>(() => pickRandomVariantIndex(heroVariantsArray.length));
  const activeHeroVariant = heroVariantsArray[heroVariantIndex] ?? { title: '', subtitle: '' };
  const currentYear = new Date().getFullYear();
  const signUpSeoUrl = '/sign-up';
  const buildLoginDestination = (extraParams?: Record<string, string>) => {
    const nextParams = new URLSearchParams(forwardedAuthParams);
    if (extraParams) {
      Object.entries(extraParams).forEach(([key, value]) => {
        nextParams.set(key, value);
      });
    }
    const nextSearch = nextParams.toString();
    return nextSearch ? `/login?${nextSearch}` : '/login';
  };
  const loginHref = buildLoginDestination();

  // const siteKey = import.meta.env.VITE_TURNSTILE_SITEKEY as string; // --- COMMENTED OUT as it's not used



  useEffect(() => {
    // Guard against duplicate attempts causing multiple toasts
    const alreadyAttempted = sessionStorage.getItem('emailVerifyAttempted') === '1';
    if (alreadyAttempted) return;

    // Prefer current URL, fall back to preserved params captured before locale redirect
    let search = location.search;
    if (!search || search.length === 0) {
      const preserved = sessionStorage.getItem('pendingEmailAction') || '';
      if (preserved) {
        search = preserved;
      }
    }

    const queryParams = new URLSearchParams(search);
    const mode = (queryParams.get('mode') || '').toLowerCase();
    const landingMode = getAdModeFromSearchParams(new URLSearchParams(`mode=${queryParams.get('landing') || ''}`));
    const oobCodeFromUrl = queryParams.get('oobCode');

    if (mode === 'verifyemail' && oobCodeFromUrl) {
      if (landingMode) {
        storePendingAdMode(landingMode);
      }
      // Extract email from oobCode before applying (for pre-filling login page)
      sessionStorage.setItem('emailVerifyAttempted', '1');
      checkActionCode(auth, oobCodeFromUrl)
        .then((info) => {
          const verifiedEmail = info?.data?.email;
          if (verifiedEmail) {
            sessionStorage.setItem('verifiedEmail', verifiedEmail);
          }
        })
        .catch(() => {}); // Non-blocking — pre-fill is a convenience, not critical
      applyActionCode(auth, oobCodeFromUrl)
        .then(async () => {
          setVerified(true);
          setIsVerificationSent(false);
          sessionStorage.setItem('emailVerifyApplied', '1');
          sessionStorage.removeItem('pendingEmailAction');

          // Attempt seamless login ONLY if a Firebase session exists and becomes verified
          // (We do not store passwords; this only succeeds when the user is still signed in.)
          try {
            const current = auth.currentUser;
            if (current) {
              await current.reload();
              if (current.emailVerified) {
                await ensureAuthUserDoc(current, {
                  provider: providerIdForUser(current, 'password'),
                  source: 'email_verification_auto_login',
                  includeDeviceInfo: false,
                });
                cacheAuthUser(current);
                toast.success(t('auth.email_verified_logging_in'), { autoClose: 3000 });
                redirectAfterLogin(navigate);
                return;
              }
            }
          } catch (e) {
            // If anything fails, fall back to login redirect with success flag
            console.warn('Auto-login after verification failed; falling back to login.', e);
            if (isEnsureUserBlockedError(e)) {
              try {
                await auth.signOut();
              } catch {}
              toast.error(ensureUserErrorMessage(e));
              navigate(buildLoginDestination());
              return;
            }
          }

          // Fallback: show success on login page
          const delayMs = parseInt(import.meta.env.VITE_VERIFY_REDIRECT_DELAY_MS || '0', 10);
          if (delayMs > 0) {
            toast.success('Email verified successfully! Redirecting to login...', { autoClose: delayMs });
            setTimeout(() => navigate(buildLoginDestination({ verified: '1' })), delayMs);
          } else {
            navigate(buildLoginDestination({ verified: '1' }));
          }
        })
        .catch((error) => {
          console.log('applyActionCode failed:', error);
          // Only show error if we haven't already successfully applied a code
          if (sessionStorage.getItem('emailVerifyApplied') !== '1') {
            toast.error(t('auth.verification_link_invalid'));
          }
        });
    } else if (mode === 'resetpassword' && oobCodeFromUrl) {
      setIsResetPassword(true);
      setOobCode(oobCodeFromUrl);
    }else{
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (!user) {
          clearCachedAuthUser();
          return;
        }
        if (!user.emailVerified) return;
        try {
          await ensureAuthUserDoc(user, {
            provider: providerIdForUser(user),
            source: 'sign_page_existing_session',
            includeDeviceInfo: false,
          });
          cacheAuthUser(user);
          if (requestedMode) {
            clearPendingAdMode();
            navigate(getPostAuthAdDestination(requestedMode));
          } else {
            redirectAfterLogin(navigate);
          }
        } catch (error) {
          console.warn('Unable to repair existing signup session before redirect.', error);
          clearCachedAuthUser();
          if (isEnsureUserBlockedError(error)) {
            try {
              await auth.signOut();
            } catch {}
            toast.error(ensureUserErrorMessage(error));
          }
        }
      });
      return () => unsubscribe();
    }
  }, [buildLoginDestination, location, navigate, requestedMode, t]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && !user.emailVerified && isVerificationSent) {
        user.reload().then(() => {
          if (user.emailVerified) {
            toast.success('Email verified successfully! You can now log in.');
            setIsVerificationSent(false);
            navigate(buildLoginDestination());
          }
        });
      }
    });

    return () => unsubscribe();
  }, [buildLoginDestination, isVerificationSent, navigate]);

  useEffect(() => {
    if (resendCooldownSeconds <= 0) return undefined;
    const intervalId = window.setInterval(() => {
      setResendCooldownSeconds((current) => (current > 1 ? current - 1 : 0));
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [resendCooldownSeconds]);

  const toggleEmailSignUp = () => {
    setIsEmailSignUp(!isEmailSignUp);
    setErrors({});
    // setTurnstileToken(null); // --- COMMENTED OUT
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (errors[name as keyof Errors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = () => {
    const newErrors: Errors = {};
    if (!formData.firstName) newErrors.firstName = t('auth.first_name_required');
    if (!formData.lastName) newErrors.lastName = t('auth.last_name_required');
    if (!formData.email) newErrors.email = t('auth.email_required');
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = t('auth.email_invalid');
    if (!formData.password) newErrors.password = t('auth.password_required');
    else if (formData.password.length < 8) newErrors.password = t('auth.password_min_length');
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = t('auth.passwords_do_not_match');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateResetPassword = () => {
    const newErrors: Errors = {};
    if (!formData.password) newErrors.password = t('auth.password_required');
    else if (formData.password.length < 8) newErrors.password = t('auth.password_min_length');
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = t('auth.passwords_do_not_match');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    // === SECURITY CHECK ===
    if (isDeviceCheckLoading) {
      toast.info(t('auth.verifying_device'));
      return;
    }
    if (isBlocked) {
      toast.error(t('auth.device_already_used'));
      return;
    }
    // === END OF CHECK ===

    setLoading(true);
    e.preventDefault();
    if (!validate()) {
      toast.error('Please correct the errors in the form');
      setLoading(false);
      return;
    }
    if (requestedMode) storePendingAdMode(requestedMode);

    // --- 3. COMMENTED OUT Turnstile Check ---
    //if (!turnstileToken) {
    // if (!turnstileToken && import.meta.env.PROD) {	
    //   toast.error('Please complete the challenge');
    //   setLoading(false);
    //   return;
    // }

    try {
      const { email, password } = formData;
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      if (!user) {
        throw new Error("User not signed in after createUserWithEmailAndPassword");
      }

      console.log("🔍 Debug - User UID:", user.uid);
      console.log("🔍 Debug - User object:", user);

      await ensureAuthUserDoc(user, {
        firstName: formData.firstName || '',
        lastName: formData.lastName || '',
        displayName: `${formData.firstName} ${formData.lastName}`,
        provider: 'password',
        source: 'email_signup',
      });
      console.log("✅ User document created successfully via backend ensure");
      // === "BURN" THE DEVICE ===
      await markDeviceAsUsed();
      trackSignupEvent('email');
      
      await sendEmailVerification(userCredential.user, {
        url: `${window.location.origin}${localizedPath('/sign-up')}?mode=verifyEmail${requestedMode ? `&landing=${requestedMode}` : ''}`,
        handleCodeInApp: true,
      });
      // setTurnstileToken(null); // --- COMMENTED OUT
      setIsVerificationSent(true);
      setResendCooldownSeconds(RESEND_COOLDOWN_SECONDS);
      setResendLimitReached(false);
      toast.success(t('auth.verification_email_sent'));
      await auth.signOut();
    } catch (error: any) {
      clearCachedAuthUser();
      try {
        await auth.signOut();
      } catch {}
      if (isEnsureUserBlockedError(error)) {
        toast.error(ensureUserErrorMessage(error));
        return;
      }
      // NEW, more detailed error handling
      let errorMessage = t('auth.unknown_error', { defaultValue: 'An unknown error occurred during sign-up.' });
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = t('auth.email_in_use');
          break;
        case 'auth/invalid-email':
          errorMessage = t('auth.email_not_valid');
          break;
        case 'auth/weak-password':
          errorMessage = t('auth.password_too_weak');
          break;
        case 'auth/operation-not-allowed':
          errorMessage = t('auth.email_auth_disabled');
          break;
        default:
          // Catch App Check token errors with a user-friendly message
          if (
            String(error?.code || '').includes('app-check') ||
            String(error?.message || '').includes('app-check') ||
            String(error?.message || '').includes('appCheck') ||
            String(error?.message || '').includes('app_check')
          ) {
            errorMessage = t('auth.session_expired');
          } else {
            errorMessage = `Error signing up: ${error.message}`;
          }
          console.error("Full signup error object:", error);
          break;
      }
      toast.error(errorMessage);
    } finally {
      // Use finally to ensure setLoading is always set to false
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (!validateResetPassword()) {
      toast.error(t('auth.reset_form_error'));
      setLoading(false);
      return;
    }

    if (!oobCode) {
      toast.error(t('auth.invalid_reset_code'));
      setLoading(false);
      return;
    }

    try {
      await confirmPasswordReset(auth, oobCode, formData.password);
      toast.success(t('auth.reset_password_success'));
      setFormData({ ...formData, password: '', confirmPassword: '' });
      setIsResetPassword(false);
      navigate('/login');
    } catch (error: any) {
      toast.error(t('auth.reset_password_error'));
    } finally {
      setLoading(false);
    }
  };

  // --- 4. COMMENTED OUT Turnstile Handler ---
  // const handleTurnstileSuccess = (token: string) => {
  //   setTurnstileToken(token);
  // };

  // Helper: detect App Check errors and show user-friendly message
  const isAppCheckError = (error: any): boolean => {
    const msg = error?.message || error?.code || '';
    return msg.includes('app-check') || msg.includes('appCheck') || msg.includes('app_check');
  };

  const handleSignupError = (error: any, provider: string) => {
    if (isEnsureUserBlockedError(error)) {
      toast.error(ensureUserErrorMessage(error));
    } else if (isAppCheckError(error)) {
      // Auto-retry: force-refresh the App Check token
      if (appCheck) {
        getToken(appCheck, true)
          .then(() => {
            toast.warning(t('auth.session_refreshed_signup', { provider }));
          })
          .catch(() => {
            toast.error(t('auth.session_expired'));
          });
      } else {
        toast.error(t('auth.session_expired'));
      }
    } else {
      toast.error(t('auth.error_signing_up_with', { provider }));
    }
  };

  const handleGoogleSignUp = async () => {
    // === SECURITY CHECK ===
    if (isDeviceCheckLoading) {
      toast.info(t('auth.verifying_device'));
      return;
    }
    if (isBlocked) {
      toast.error(t('auth.device_already_used'));
      return;
    }
    // === END OF CHECK ===

    try {
      if (requestedMode) storePendingAdMode(requestedMode);
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      await ensureAuthUserDoc(user, {
        provider: providerIdForUser(user, 'google.com'),
        source: 'google_signup',
      });
      await markDeviceAsUsed();
      cacheAuthUser(user);

      toast.success(t('auth.signed_up_with_google'));
      trackSignupEvent('google');
      redirectAfterLogin(navigate);
    } catch (error: any) {
      clearCachedAuthUser();
      try {
        await auth.signOut();
      } catch {}
      handleSignupError(error, 'Google');
    }
  };

  const handleMicrosoftSignUp = async () => {
    // === SECURITY CHECK ===
    if (isDeviceCheckLoading) {
      toast.info(t('auth.verifying_device'));
      return;
    }
    if (isBlocked) {
      toast.error(t('auth.device_already_used'));
      return;
    }
    // === END OF CHECK ===

    try {
      if (requestedMode) storePendingAdMode(requestedMode);
      const result = await signInWithPopup(auth, microsoftProvider);
      const user = result.user;
      await ensureAuthUserDoc(user, {
        provider: providerIdForUser(user, 'microsoft.com'),
        source: 'microsoft_signup',
      });
      await markDeviceAsUsed();
      cacheAuthUser(user);

      toast.success(t('auth.signed_up_with_microsoft'));
      trackSignupEvent('microsoft');
      redirectAfterLogin(navigate);
    } catch (error: any) {
      clearCachedAuthUser();
      try {
        await auth.signOut();
      } catch {}
      handleSignupError(error, 'Microsoft');
    }
  };

  const handleResendVerification = async () => {
    if (resendCooldownSeconds > 0) {
      toast.info(
        t('auth.resend_wait_time', {
          defaultValue: "You can resend the email in {{time}} if it hasn't arrived.",
          time: formatResendWait(resendCooldownSeconds),
        })
      );
      return;
    }

    try {
      const { email, password } = formData;
      
     const userCredential = await signInWithEmailAndPassword(auth, email, password);
            await sendEmailVerification(userCredential.user);
            await auth.signOut();
      setResendLimitReached(false);
      toast.success(t('auth.verification_email_resent'));
      setResendCooldownSeconds(RESEND_COOLDOWN_SECONDS);
  
    } catch (error: any) {
      if (error.code === 'auth/email-already-in-use') {
        toast.error(t('auth.email_already_in_use_try_login'));
      } else if (error.code === 'auth/too-many-requests') {
        setResendCooldownSeconds(0);
        setResendLimitReached(true);
        toast.warning(
          t('auth.resend_limit_reached_toast', {
            defaultValue: 'Too many verification emails were requested recently. Please check your inbox or spam folder, or use a different email address.',
          })
        );
      } else {
        console.log(error)
        toast.error(t('auth.resend_later', { defaultValue: 'We could not resend the email right now. Please try again shortly.' }));
      }
    }
  };

  const signUpCardHeader = (
    <div className="text-center">
      {isAdTraffic ? (
        <div className="flex items-center justify-center gap-2">
          <img src="/logo.png" alt="Sarvalok" className="h-10 w-auto object-contain" />
          <span
            className="text-[26px] leading-none text-slate-900"
            style={{ fontFamily: "'General Sans', 'Inter', 'Segoe UI', -apple-system, sans-serif", fontWeight: 600 }}
          >
            Sarvalok
          </span>
        </div>
      ) : (
        <h2 className="text-3xl font-extrabold">{t('auth.title')}</h2>
      )}
      <br />
      <p className="text-sm text-gray-600">
        {t('auth.tagline1')}
      </p>
      {!isAdTraffic ? (
        <div className="mt-3">
          <span className="inline-flex items-center justify-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
            {t('auth.no_credit_card')}
          </span>
        </div>
      ) : null}
      {verified && <p className="text-m my-2 rounded-lg bg-green-50 text-green-500">{t('auth.email_verified_redirecting')}</p>}
      <ToastContainer position="bottom-right" />
    </div>
  );

  const signUpFormContent = (
    <>
      {signUpCardHeader}
      {!isEmailSignUp ? (
        <div className="mt-4 space-y-6">
          <button
            onClick={toggleEmailSignUp}
            className="w-full rounded-md bg-black px-4 py-2 text-white transition-colors hover:bg-gray-800"
          >
            {t('auth.sign_up_with_email')}
          </button>
          <div className="mt-4 text-center text-sm text-gray-600">
            <p>{t('auth.or_continue_with')}</p>
          </div>
          <div style={{ flexDirection: 'column' }} className="mt-2 flex justify-center space-x-4">
            <button
              onClick={handleGoogleSignUp}
              style={{ fontSize: '14px', whiteSpace: 'pre', fontWeight: 500 }}
              className="btn-hover-auth m-2 flex items-center justify-center rounded-full border border-gray-300 px-4 py-2 text-gray-800"
            >
              <GoogleIcon className="mr-2 h-5 w-5" />
              {t('auth.sign_up_with_google')}
            </button>
            <button
              onClick={handleMicrosoftSignUp}
              style={{ fontSize: '14px', whiteSpace: 'pre', fontWeight: 500 }}
              className="btn-hover-auth m-2 flex items-center justify-center rounded-full border border-gray-300 px-4 py-2 text-gray-800"
            >
              <Microsoft className="mr-2 h-5 w-5" />
              {t('auth.sign_up_with_microsoft')}
            </button>
          </div>
        </div>
      ) : (
        <form className="mt-4 space-y-6" onSubmit={handleEmailSignUp}>
          <div className="flex flex-col rounded-md border border-gray-300 px-3 py-2">
            <label className="text-gray-700">{t('auth.first_name')}</label>
            <input
              type="text"
              name="firstName"
              maxLength={40}
              placeholder={t('auth.type_here')}
              className="w-full focus:outline-none"
              value={formData.firstName}
              onChange={handleChange}
            />
            {errors.firstName && <p className="text-sm text-red-500">{errors.firstName}</p>}
          </div>
          <div className="mt-4 flex flex-col rounded-md border border-gray-300 px-3 py-2">
            <label className="text-gray-700">{t('auth.last_name')}</label>
            <input
              type="text"
              name="lastName"
              className="w-full focus:outline-none"
              value={formData.lastName}
              onChange={handleChange}
            />
            {errors.lastName && <p className="text-sm text-red-500">{errors.lastName}</p>}
          </div>
          <div className="mt-4 flex flex-col rounded-md border border-gray-300 px-3 py-2">
            <label className="text-gray-700">{t('auth.email')}</label>
            <input
              type="email"
              name="email"
              className="w-full focus:outline-none"
              value={formData.email}
              onChange={handleChange}
            />
            {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
          </div>
          <div className="mt-4 flex flex-col rounded-md border border-gray-300 px-3 py-2">
            <label className="text-gray-700">{t('auth.password')}</label>
            <input
              type="password"
              name="password"
              placeholder={t('auth.password_placeholder')}
              className="w-full focus:outline-none"
              value={formData.password}
              onChange={handleChange}
            />
            {errors.password && <p className="text-sm text-red-500">{errors.password}</p>}
          </div>
          <div className="mt-4 flex flex-col rounded-md border border-gray-300 px-3 py-2">
            <label className="text-gray-700">{t('auth.confirm_password')}</label>
            <input
              type="password"
              name="confirmPassword"
              placeholder={t('auth.confirm_password_placeholder')}
              className="w-full focus:outline-none"
              value={formData.confirmPassword}
              onChange={handleChange}
            />
            {errors.confirmPassword && <p className="text-sm text-red-500">{errors.confirmPassword}</p>}
          </div>
          <div className="mt-4 flex items-center gap-2">
            <button
              type="submit"
              className="mt-4 w-full rounded-md bg-black px-4 py-2 text-white transition-colors hover:bg-gray-800"
              disabled={loading}
            >
              {loading ? <span className="spinner" /> : t('auth.sign_up_btn')}
            </button>
            <button
              onClick={toggleEmailSignUp}
              className="mt-4 w-full rounded-md bg-black px-4 py-2 text-white transition-colors hover:bg-gray-800"
            >
              {t('auth.back')}
            </button>
          </div>
        </form>
      )}
      <div className="text-center text-sm text-gray-600">
        <p>
          <LocalizedLink to={loginHref} className="underline hover:text-gray-700">{t('auth.already_have_account')}</LocalizedLink>.
        </p>
      </div>
      <div className="text-center text-sm text-gray-600">
        <p>
          {t('auth.agree_signup_text')}{' '}
          <LocalizedLink to="/terms-condition" className="underline hover:text-gray-700">{t('auth.terms')}</LocalizedLink>{' '}{t('auth.agree_signup_and')}{' '}
          <LocalizedLink to="/privacy-policy" className="underline hover:text-gray-700">{t('auth.privacy')}</LocalizedLink>
          .
        </p>
      </div>
      {/* Ad-landing shells omit the site footer (App.tsx route split), so the
          store badges live here for paid traffic only — organic /sign-up gets
          them from the shared Footer instead (no duplicates). */}
      {isAdTraffic ? <StoreBadges className="mt-4 justify-center flex-wrap" height={40} /> : null}
    </>
  );

  const verificationCardContent = (
    <>
      {signUpCardHeader}
      <div className="rounded-lg bg-white p-6 shadow-md">
        <h3 className="mb-4 text-center text-xl font-bold">{t('auth.verify_email_title')}</h3>
        <p className="mb-6 text-center">
          {t('auth.verify_email_msg', { email: formData.email })}
        </p>
        <div className="mb-4 text-center">
          {resendLimitReached ? (
            <div className="space-y-2 text-center">
              <p className="text-sm font-semibold text-amber-700">
                {t('auth.resend_limit_reached_title', {
                  defaultValue: 'Verification email limit reached',
                })}
              </p>
              <p className="text-sm text-gray-500">
                {t('auth.resend_limit_reached_body', {
                  defaultValue: "You've requested too many verification emails for this address. Please check your inbox and spam folder. If you still can't find it, please wait at least an hour before trying again, or sign up with a different email address.",
                })}
              </p>
            </div>
          ) : resendCooldownSeconds > 0 ? (
            <p className="text-sm text-gray-500">
              {t('auth.resend_available_in', {
                defaultValue: "You can resend the confirmation email in {{time}} if it hasn't arrived yet.",
                time: formatResendWait(resendCooldownSeconds),
              })}
            </p>
          ) : (
            <>
              <button
                onClick={handleResendVerification}
                className="text-blue-600 hover:underline focus:outline-none"
              >
                {t('auth.resend_email')}
              </button>
              <p className="mt-2 text-xs text-gray-500">
                {t('auth.resend_email_hint', {
                  defaultValue: '(Please check your spam or bulk mail folder as well.)',
                })}
              </p>
            </>
          )}
        </div>
        <button
          onClick={() => {
            setIsVerificationSent(false);
            setIsEmailSignUp(false);
            setResendCooldownSeconds(0);
            setResendLimitReached(false);
          }}
          className="mt-4 w-full rounded-md bg-black px-4 py-2 text-white transition-colors hover:bg-gray-800"
        >
          {t('auth.back_to_sign_up')}
        </button>
      </div>
    </>
  );

  const renderAdShell = (cardContent: React.ReactNode) => (
    <div className="min-h-screen bg-stone-950 text-white">
      <SEO
        title={isVerificationSent ? t('auth.verify_email_page_title') : t('auth.signup_page_title')}
        description={isVerificationSent ? t('auth.verify_email_page_desc') : t('auth.signup_page_desc')}
        url={signUpSeoUrl}
        noIndex={isVerificationSent}
      />
      <div className="relative flex min-h-screen flex-col overflow-hidden">
        {heroImage ? (
          <img
            src={heroImage}
            alt=""
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${isHeroLoaded ? 'opacity-100' : 'opacity-0'}`}
            loading="eager"
            fetchPriority="high"
            onLoad={() => setIsHeroLoaded(true)}
          />
        ) : null}
        <div className="absolute inset-0 bg-black/40 lg:bg-gradient-to-r lg:from-black/65 lg:via-black/40 lg:to-black/10" />
        <div className="relative flex flex-1 flex-col lg:flex-row lg:items-center lg:justify-between">
          <div className="px-5 pt-5 text-white sm:px-8 lg:max-w-[560px] lg:px-14 lg:pt-16">
            <div className="rounded-[28px] bg-black/35 px-5 py-5 backdrop-blur-[2px] lg:bg-transparent lg:px-0 lg:py-0 lg:backdrop-blur-0">
              <p className="mb-3 text-[11px] uppercase tracking-[0.24em] text-white/70 lg:mb-4 lg:text-xs">
                {t(`auth.hero.${adHeroKey}.eyebrow`)}
              </p>
              <h1 className="max-w-[14ch] font-serif text-3xl leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl lg:leading-[1.02]">
                {activeHeroVariant.title}
              </h1>
              <p className="mt-4 max-w-[42ch] text-sm leading-6 text-white/80 lg:text-base lg:leading-7">
                {activeHeroVariant.subtitle}
              </p>
            </div>
          </div>
          <div className="relative z-10 px-5 pb-8 pt-5 sm:px-8 lg:w-[460px] lg:px-0 lg:pb-0 lg:pr-10 lg:pt-0">
            <div className="rounded-[28px] bg-white/95 p-6 text-[#111111] shadow-2xl backdrop-blur-sm lg:p-8">
              <div className="w-full space-y-6 rounded-md">
                {cardContent}
              </div>
            </div>
          </div>
        </div>
        <div className="relative z-10 border-t border-black/10 bg-white/95 px-4 py-3 text-xs text-slate-600 backdrop-blur-sm">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-5 gap-y-2 text-center">
            <span>{t('footer:footer.signup_copyright', { year: currentYear, defaultValue: `© ${currentYear} Sarvalok Inc. All rights reserved.` })}</span>
            <LocalizedLink to="/" className="hover:text-slate-900">{t('nav:nav.home', { defaultValue: 'Home' })}</LocalizedLink>
            <LocalizedLink to="/privacy-policy" className="hover:text-slate-900">{t('footer:footer.privacy_policy')}</LocalizedLink>
            <LocalizedLink to="/terms-condition" className="hover:text-slate-900">{t('footer:footer.terms_conditions')}</LocalizedLink>
            <LocalizedLink to="/contact-us" className="hover:text-slate-900">{t('footer:footer.contact')}</LocalizedLink>
          </div>
        </div>
      </div>
    </div>
  );

  if (isResetPassword) {
    return (
      <div className="auth-container">
        <SEO
          title={t('auth.reset_password_page_title')}
          description={t('auth.reset_password_page_desc')}
          url={signUpSeoUrl}
          noIndex
        />
        <div className="overlay"></div>
        <div className="content">
          <div className="w-full max-w-md space-y-6 rounded-md text-[#111111]">
            <div className="text-center">
              <h2 className="text-3xl font-extrabold">{t('auth.title')}</h2>
              <br />
              <p className="text-sm text-gray-600">
              {t('auth.tagline1')}
              </p>
              <ToastContainer position="bottom-right" />
            </div>

            <form className="bg-white p-6 rounded-lg shadow-md" onSubmit={handleResetPassword}>
              <h3 className="text-xl font-bold text-center mb-4">{t('auth.reset_password_title')}</h3>
              <p className="text-center mb-6">{t('auth.reset_password_subtitle')}</p>

              <div className="flex flex-col border border-gray-300 rounded-md px-3 py-2 mt-4">
                <label className="text-gray-700">{t('auth.new_password')}</label>
                <input
                  type="password"
                  name="password"
                  placeholder={t('auth.password_placeholder', { defaultValue: 'Minimum 8 characters' })}
                  className="w-full focus:outline-none"
                  value={formData.password}
                  onChange={handleChange}
                />
                {errors.password && <p className="text-red-500 text-sm">{errors.password}</p>}
              </div>

              <div className="flex flex-col border border-gray-300 rounded-md px-3 py-2 mt-4">
                <label className="text-gray-700">{t('auth.confirm_new_password')}</label>
                <input
                  type="password"
                  name="confirmPassword"
                  placeholder={t('auth.confirm_password_placeholder', { defaultValue: 'Re-enter your password' })}
                  className="w-full focus:outline-none"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
                {errors.confirmPassword && (
                  <p className="text-red-500 text-sm">{errors.confirmPassword}</p>
                )}
              </div>

              <button
                type="submit"
                className="w-full py-2 px-4 bg-black text-white rounded-md hover:bg-gray-800 transition-colors mt-6"
                disabled={loading}
              >
                {loading ? <span className="spinner" /> : t('auth.reset_password_btn')}
              </button>

              <button
                onClick={() => {
                  setIsResetPassword(false);
                  navigate('/login');
                }}
                className="w-full py-2 px-4 bg-gray-200 text-black rounded-md hover:bg-gray-300 transition-colors mt-4"
              >
                {t('auth.back_to_login')}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (isVerificationSent) {
    if (isAdTraffic) {
      return renderAdShell(verificationCardContent);
    }

    return (
      <div className="auth-container">
        <SEO
          title={t('auth.verify_email_page_title')}
          description={t('auth.verify_email_page_desc')}
          url={signUpSeoUrl}
          noIndex
        />
        <div className="overlay"></div>
        <div className="content">
          <div className="w-full max-w-md space-y-6 rounded-md text-[#111111]">
            {verificationCardContent}
          </div>
        </div>
      </div>
    );
  }

  if (isAdTraffic) {
    return renderAdShell(signUpFormContent);
  }

  return (
    <div className="auth-container">
      <SEO
        title={t('auth.signup_page_title')}
        description={t('auth.signup_page_desc')}
        url={signUpSeoUrl}
      />
      <div className="overlay"></div>
      <div className={`content ${isEmailSignUp ? 'small-padding' : ''}`}>
        <div className="w-full max-w-md space-y-6 rounded-md text-[#111111]">
          {signUpFormContent}
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
